/**
 * Base utilities
 *	@module ods/js/ods
 */

/* eslint-disable jsdoc/text-escaping */

//
//	Requirements
//

import {configure} from 'safe-stable-stringify';
import {isLanguageGerman as _isLanguageGerman} from '@alias_root/src/framework/ods/locale';
import {trimRight} from '@alias_root/src/framework/ods/string';
// eslint-disable-next-line boundaries/dependencies
import escape from 'lodash/escape.js';
import {type TableEngine} from '@alias_root/src/data/tableEngineEnum';
import {type sessionType} from '@alias_root/src/data/session';
import {getGlobalObject} from './globalObject';
import {z} from 'zod';

//
//	Types
//

//
//	Constants
//

const stringify = configure({});
const DEFAULT_LANGUAGE_CODE_A2 = 'EN';
const DEFAULT_COUNTRY_CODE_A2 = 'AT';
const DEFAULT_DECIMAL_SEPARATOR = '.';
const DEFAULT_THOUSANDS_SEPARATOR = ',';

//
//	API
//

/**
 * Assert that all possible cases in a switch statement are handled at compile and runtime.
 *
 * @param value - Value.
 */
export const unreachableCase = (value: never): never => {
	throw new Error(`unreachableCase with value "${value}"`);
};

/**
 * Stringify any JavaScript value.
 *
 * @param value - Value to stringify.
 * @returns Strigified value.
 */
export const prettyFormat = (value: unknown): string => {
	try {
		const text = stringify(value, null, 2);
		return text ?? '';
	} catch {
		return '';
	}
};

/**
 *	Get session object
 *
 * @returns Session configuration.
 */
export const getSession = (): sessionType => {
	const globalPeriscopeObject = getGlobalObject() as {session?: unknown};
	const session = globalPeriscopeObject.session as sessionType;

	if (!session) {
		throw new Error('Session is not defined');
	}

	return session;
};

/**
 *	Are experimental features enabled
 *	@returns true when experimental features are enabled or else false.
 */
export const hasExperimentalFeatures = (): boolean => getSession().userPreferences.experimentalFeatures;

/**
 *	Get the table engine.
 *	@returns the table engine selected by user in user preferences.
 */
export const getTableEngine = (): TableEngine => getSession().userPreferences.tableEngine as TableEngine;

/**
 *	Get module options
 *
 *  @param schema - The zod type definition
 *	@returns The module options
 */
export function getModuleOptions<T>(schema: z.ZodType<T>): T {
	const globalPeriscopeObject = getGlobalObject() as {moduleOptions?: unknown};
	const result = schema.safeParse(globalPeriscopeObject.moduleOptions);

	if (result.success) {
		return result.data;
	} else {
		throw new Error(z.prettifyError(result.error));
	}
}

/**
 *	Get the current language code
 *
 *	@returns	The current language code A2
 */
export function getLanguageCodeA2(): string {
	const SESSION = getSession();

	if (SESSION && typeof SESSION.languageCodeA2 === 'string') {
		return SESSION.languageCodeA2;
	}

	return DEFAULT_LANGUAGE_CODE_A2;
}

/**
 * Is the language german
 *
 * @returns	Return true if the language is german or else false.
 */
export function isLanguageGerman(): boolean {
	return _isLanguageGerman(getLanguageCodeA2());
}

/**
 * Get the current territory
 *
 * @returns	The current territory code A2
 */
export function getTerritoryCodeA2(): string {
	const SESSION = getSession();

	if (SESSION && typeof SESSION.territoryCodeA2 === 'string' && SESSION.territoryCodeA2.length === 2) {
		return SESSION.territoryCodeA2;
	}

	return DEFAULT_COUNTRY_CODE_A2;
}

/**
 * Converts the characters `&`, `<`, `>`, `"`, and `'` in string to their corresponding HTML entities.
 *
 * @param value - String.
 * @returns The escaped string.
 * @example const html = `<div>${escapeHtml('foo <> bar')}</div>`; -> 'foo &lt;&gt; bar'
 */
export const escapeHtml = (value: string): string => escape(value);

/**
 * Converts the character '"' in string to their corresponding HTML entities.
 *
 * @param value - String.
 * @returns The escaped string.
 * @example const html = `<div title="${escapeText('foo <> bar"')}"></div>`; -> '&quot;foo <> bar&quot;'
 */
export const escapeText = (value: string): string => value.replaceAll('"', '&quot;');

/**
 * Return the text in the current system language
 *
 * @param en - Text in english
 * @param de - Text in german
 * @returns The string in the current system language
 * @example const text = tr('hello', 'hallo');
 */
export function tr(en: string, de?: string): string {
	if (getLanguageCodeA2().toUpperCase() === 'DE' && de) {
		return de;
	}

	return en;
}

/**
 * Set the current system path
 *
 * @returns The current Path
 */
export function resourcePath(): string {
	const session = getSession();
	const path = session && typeof session.resourcePath === 'string' ? session.resourcePath : '';

	return trimRight(path, '/');
}

/**
 * Get the full url for a resource
 *
 * @param filename - The filename.
 * @returns The full url.
 */
export function getResourceUrl(filename: string): string {
	return `${resourcePath()}/${filename || ''}`;
}

/**
 * Get the decimal separator
 *
 * @returns The decimal separator
 */
export function getDecimalSeparator(): string {
	const aSession = getSession();

	return aSession && typeof aSession.decimalSeparator === 'string' ? aSession.decimalSeparator : DEFAULT_DECIMAL_SEPARATOR;
}

/**
 * Get the thousands separator
 *
 * @returns The thousands separator
 */
export function getThousandsSeparator(): string {
	const aSession = getSession();

	return aSession && typeof aSession.thousandsSeparator === 'string' ? aSession.thousandsSeparator : DEFAULT_THOUSANDS_SEPARATOR;
}
